import { useState, useEffect } from 'react';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';

export const useBlogAuthors = (blogs = []) => {
  const [authors, setAuthors] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAuthors = async () => {
      if (blogs.length === 0) return;

      setLoading(true);
      setError(null);

      try {
        // Get unique author IDs from blogs (check both userId and author fields)
        const authorIds = [...new Set(blogs.map(blog => blog.userId || blog.author).filter(Boolean))];
        
        if (authorIds.length === 0) {
          setLoading(false);
          return;
        }

        // Fetch author details
        const authorPromises = authorIds.map(async (userId) => {
          try {
            const userDoc = await getDoc(doc(db, 'users', userId));
            if (userDoc.exists()) {
              return {
                id: userId,
                ...userDoc.data()
              };
            }
            return {
              id: userId,
              name: 'Unknown User',
              email: 'unknown@example.com'
            };
          } catch (error) {
            console.error(`Error fetching user ${userId}:`, error);
            return {
              id: userId,
              name: 'Unknown User',
              email: 'unknown@example.com'
            };
          }
        });

        const authorData = await Promise.all(authorPromises);
        
        // Convert to object for easy lookup
        const authorsMap = {};
        authorData.forEach(author => {
          authorsMap[author.id] = author;
        });

        setAuthors(authorsMap);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching authors:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAuthors();
  }, [blogs]);

  // Helper functions
  const getAuthor = (userId) => {
    return authors[userId] || {
      id: userId,
      name: 'Unknown User',
      email: 'unknown@example.com'
    };
  };

  const getAuthorName = (userId) => {
    const author = getAuthor(userId);
    return author.name || author.email?.split('@')[0] || 'Unknown';
  };

  // Get author ID from blog (check both userId and author fields)
  const getAuthorId = (blog) => {
    return blog.userId || blog.author;
  };

  const getAuthorStats = () => {
    const stats = {
      totalAuthors: Object.keys(authors).length,
      activeAuthors: 0,
      topAuthors: []
    };

    // Count posts per author
    const authorPostCounts = {};
    blogs.forEach(blog => {
      const authorId = getAuthorId(blog);
      if (authorId) {
        authorPostCounts[authorId] = (authorPostCounts[authorId] || 0) + 1;
      }
    });

    // Get top authors
    stats.topAuthors = Object.entries(authorPostCounts)
      .map(([userId, postCount]) => ({
        ...getAuthor(userId),
        postCount
      }))
      .sort((a, b) => b.postCount - a.postCount)
      .slice(0, 5);

    stats.activeAuthors = Object.keys(authorPostCounts).length;

    return stats;
  };

  return {
    authors,
    loading,
    error,
    getAuthor,
    getAuthorName,
    getAuthorId,
    getAuthorStats
  };
};
